// Multi-client session sync ("dumb relay" - see the plan). This module owns
// the room connection and the version counter; it has no idea what a
// "game" is, it just ships/receives opaque envelope objects. app.js decides
// what an envelope means (see handleIncomingEnvelope there) and calls
// pushEnvelope() after every local change worth sharing.

let code = null;
let version = 0;
let observer = false;
let eventSource = null;
let onEnvelopeCallback = null;
// Set by hostRoom() - the host's own first SSE message is just the room's
// state as the host itself left it (a blank "idle" room, or the game it just
// published), never news, so it must not be applied back onto the host.
let skipNextMessage = false;
// Identifies this page load's own pushes to itself over SSE (see
// connectEvents' onmessage) - not a user/device id, just needs to be unique
// enough that no other tab/client picks the same value in the same room.
const clientId = Math.random().toString(36).slice(2);

export function isInSession() {
    return !!code;
}

export function isObserver() {
    return observer;
}

// Flips this client between controlling and observing without leaving the
// room - the connection/version/room code are untouched, this only changes
// whether pushEnvelope() below is a no-op and what app.js reports to the
// game controller on its next mount. Callers still need to actually
// re-render (app.js remounts the current game, if any) - this alone is just
// the flag.
export function setObserver(value) {
    observer = value;
}

export function currentRoomCode() {
    return code;
}

// Registers the single handler invoked whenever a new envelope arrives,
// whether from our own push, another client's push, or the initial
// hydration on join/reconnect. Only one caller (app.js) needs this.
export function onEnvelope(callback) {
    onEnvelopeCallback = callback;
}

function connectEvents() {
    eventSource = new EventSource(`/api/rooms/${code}/events`);
    eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.error) return; // room gone - nothing more we can do here
        version = data.version;
        if (skipNextMessage) {
            skipNextMessage = false;
            return;
        }
        // Our own push echoing back - we already rendered this exact state
        // the moment we made the change, no need (and no reason) to render
        // it again. Deliberately an identity check, not "is this version
        // newer than what we've already applied" - this SSE message can and
        // regularly does arrive before our own pushEnvelope() below even
        // finishes awaiting its POST response (the SSE connection is
        // already open; the server broadcasts synchronously before that
        // response is even sent), so comparing against a `version` this
        // client hasn't locally updated yet would still (wrongly) let it
        // through and double-render - which, for e.g. Killer's life-dot
        // pulse animation, silently cancels the animation the direct local
        // render already started, since the exact-same-state second render
        // happens before the browser gets to paint the first one.
        if (data.writerId === clientId) return;
        if (onEnvelopeCallback) onEnvelopeCallback(data.state);
    };
    // EventSource retries on its own; nothing to do here on error beyond
    // letting it reconnect, at which point the server resends current state.
}

// `initialEnvelope` (optional) is published to the new room before anyone can
// join it, so a game already in progress on the host becomes the room's
// state instead of the room starting out blank.
export async function hostRoom(initialEnvelope = null) {
    const res = await fetch('/api/rooms', { method: 'POST' });
    const data = await res.json();
    code = data.code;
    version = data.version;
    observer = false;
    if (initialEnvelope) await pushEnvelope(initialEnvelope);
    skipNextMessage = true;
    connectEvents();
    return code;
}

export async function joinRoom(joinCode, { asObserver = false } = {}) {
    const res = await fetch(`/api/rooms/${joinCode}`);
    if (!res.ok) return false;
    const data = await res.json();
    code = joinCode.toUpperCase();
    version = data.version;
    observer = asObserver;
    connectEvents();
    if (onEnvelopeCallback) onEnvelopeCallback(data.state);
    return true;
}

export function leaveRoom() {
    if (eventSource) eventSource.close();
    eventSource = null;
    code = null;
    version = 0;
    observer = false;
    skipNextMessage = false;
}

// Fire-and-forget on purpose - a lost push isn't fatal, the next change (or
// the periodic reality of every other client's own pushes) will catch it up.
// A 409 means we raced someone else's write; refetch so our *next* push at
// least starts from the real current version instead of looping forever.
export async function pushEnvelope(envelope) {
    if (!code || observer) return;
    let res;
    try {
        res = await fetch(`/api/rooms/${code}/state`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version, state: envelope, clientId })
        });
    } catch (e) {
        return; // offline - same as any other lost push, the next change catches it up
    }
    if (res.status === 409) {
        // Someone else's write beat ours - pick up whatever's actually
        // current now (its own broadcast may or may not have arrived over
        // SSE yet) rather than leaving the local view stale until it does.
        const fresh = await fetch(`/api/rooms/${code}`);
        if (fresh.ok) {
            const data = await fresh.json();
            version = data.version;
            if (onEnvelopeCallback) onEnvelopeCallback(data.state);
        }
        return;
    }
    const data = await res.json();
    version = data.version;
}
